import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';

export interface InstitutionProfile {
  iid: number;
  reportDate: string;
  name: string;
  city: string;
  state: string;
  label: string;
  charterNumber: number;
  creditUnionType: string;
  typeOfMembership: number;
  websiteAddress: string | null;
  assetGrowth3Yoy: number;
  annualAssetGrowth: number;
  totalAssetsAvg: number;
  totalAssetsQe: number;
  totalLiabilitiesAvg: number;
  totalLiabilitiesQe: number;
  totalLoansAvg: number;
  totalLoansQe: number;
  totalDepositsAvg: number;
  totalDepositsQe: number;
  roaQe: number;
  roeQe: number;
  yeaQe: number;
  cofQe: number;
  efficiencyRatioQe: number;
  netIntMarginQe: number;
  netOverhead: number;
  yldOnLnsQe: number;
  cstDepQe: number;
  nonIntIncToTotalAssetsQe: number;
  texasRatioQe: number;
  commlInd: number;
  creInd: number;
  resiInd: number;
  autoInd: number;
  newAutoInd: number;
  usedAutoInd: number;
  firstTimeHomebuyer: number;
  creditBuilderRepairMain: number;
  mortgageSeller: number;
  leaseInd: number;
  studentInd: number;
  ccInd: number;
  memberGrowth: number;
  numberOfBranches: number;
  assetsEmployee: number;
  lnsPerEmp: number;
  depPerEmp: number;
  branchProd: number;
  revPerEmp: number;
  lnsToDep: number;
  isAdded: boolean;
  isRemoved: boolean;
}

export interface SimilarInstitutionsRequest {
  reportDate: string;
  states: string[];
  typesOfMembership: number[];
}

export interface TargetedInstitutionsRequest {
  reportDate: string;
  states: string[];
  typesOfMembership: number[];
  lowIncInd: boolean;
  useAssets: boolean;
  assetsMin?: number;
  assetsMax?: number;
  useLnsToDep: boolean;
  lnsToDepMin?: number;
  lnsToDepMax?: number;
  useAssetGrowth: boolean;
  assetGrowthMin?: number;
  assetGrowthMax?: number;
  resiInd: boolean;
  newAutoInd: boolean;
  usedAutoInd: boolean;
  creInd: boolean;
  firstTimeHomebuyer: boolean;
  creditBuilderRepairMain: boolean;
  mortgageSeller: boolean;
  leaseInd: boolean;
  studentInd: boolean;
  commlInd: boolean;
  ccInd: boolean;
  roeFilter: number;
  roeMin?: number;
  roeMax?: number;
  roaFilter: number;
  roaMin?: number;
  roaMax?: number;
  netIntMarginFilter: number;
  netIntMarginMin?: number;
  netIntMarginMax?: number;
  netOverheadFilter: number;
  netOverheadMin?: number;
  netOverheadMax?: number;
  yeaFilter: number;
  yeaMin?: number;
  yeaMax?: number;
  yldOnLnsFilter: number;
  yldOnLnsMin?: number;
  yldOnLnsMax?: number;
  cofFilter: number;
  cofMin?: number;
  cofMax?: number;
  cstDepFilter: number;
  cstDepMin?: number;
  cstDepMax?: number;
  texasRatioFilter: number;
  texasRatioMin?: number;
  texasRatioMax?: number;
  memberGrowthFilter: number;
  memberGrowthMin?: number;
  memberGrowthMax?: number;
}

export interface PeerGroupSelection {
  selectionCriteria: string;
  institutionProfiles: InstitutionProfile[];
}

@Injectable({
  providedIn: 'root'
})
export class InstitutionProfileService {
  private apiService = inject(ApiService);

  constructor() { }

  public getInstitutionProfile(iid: number): Observable<InstitutionProfile> {
    return this.apiService.get<InstitutionProfile>(`/api/${iid}/profile`);
  }

  public getInstitutionProfiles(iid: number): Observable<InstitutionProfile[]> {
    return this.apiService.get<InstitutionProfile[]>(`/api/${iid}/profile/all`);
  }

  public getGroupInstitutionProfiles(iid: number, groupId: string): Observable<InstitutionProfile[]> {
    return this.apiService.get<InstitutionProfile[]>(`/api/${iid}/profile/${groupId}`);
  }

  public getSimilarInstitutions(iid: number, model: SimilarInstitutionsRequest): Observable<PeerGroupSelection> {
    return this.apiService.post<PeerGroupSelection>(`/api/${iid}/profile/ncua/similar`, model);
  }

  public getTargetedInstitutions(iid: number, model: TargetedInstitutionsRequest): Observable<PeerGroupSelection> {
    return this.apiService.post<PeerGroupSelection>(`/api/${iid}/profile/ncua/targeted`, model);
  }
}
